import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';

import { recordUiError } from './diagnostics-store';

export const OPEN_BUG_REPORT_EVENT = 'domcrm:open-bug-report';

type State = { hasError: boolean; message: string };

/**
 * Корневой ErrorBoundary staff SPA: вместо белого экрана — понятный fallback и кнопка ⚠.
 * Кнопка открывает форму баг-репорта через глобальное событие (BugReportButton слушает его).
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    recordUiError(
      error instanceof Error ? error.message : error,
      `${error instanceof Error ? error.stack ?? '' : ''}\n${info.componentStack ?? ''}`,
      'error'
    );
  }

  private openBugReport = (): void => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(OPEN_BUG_REPORT_EVENT));
    }
  };

  private reload = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6" role="alert">
        <div className="w-full max-w-lg rounded-lg border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-6 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-[hsl(var(--ui-foreground))]">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            <h1 className="text-lg font-semibold">Что-то пошло не так</h1>
          </div>
          <p className="mb-4 text-sm text-[hsl(var(--ui-muted-foreground))]">
            Страница не смогла отрисоваться. Можно перезагрузить её или сообщить об ошибке — контекст
            приложится автоматически.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.openBugReport}
              className="inline-flex h-9 items-center gap-2 rounded-sm bg-[hsl(var(--ui-primary))] px-3 text-sm font-medium text-[hsl(var(--ui-primary-foreground))]"
            >
              <AlertTriangle className="h-4 w-4" aria-hidden />
              Сообщить об ошибке
            </button>
            <button
              type="button"
              onClick={this.reload}
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-[hsl(var(--ui-border))] px-3 text-sm font-medium"
            >
              <RotateCw className="h-4 w-4" aria-hidden />
              Перезагрузить
            </button>
          </div>
        </div>
      </div>
    );
  }
}
